import React, { useMemo } from "react";
import PropTypes from "prop-types";
import useSWR from "swr";
import {
  getCasesByPhase,
  getCasesByPetitionerLawfirmInstitutionOutcome,
  getCasesByPatentOwnerLawfirmInstitutionOutcome,
  getCasesByAuthorJudgeInstitutionOutcome,
  getCasesByPanelJudgeInstitutionOutcome,
  getCasesByPetitionerLawfirmFWDOutcome,
  getCasesByPatentOwnerLawfirmFWDOutcome,
  getCasesByAuthorJudgeFWDOutcome,
  getCasesByPanelJudgeFWDOutcome,
  getCasesByTechCenter,
  getCasesByIndustry,
  getCasesByPetEntityType,
  getCasesByPOEntityType,
  getCasesByPOEntityTypeOverYears,
  getCasesByTypeOverYears,
  getCasesByInstitutionOutcomeOverYears,
  getCasesByClaimInstitutionOutcome,
  getCasesByClaimDecisionOutcome,
  getCasesByPetitionerLawfirmPerformance,
  getCasesByPatentOwnerLawfirmPerformance,
  getCasesByDate,
  getCasesByDateAndField
} from "@/services/api-ptab";
import { SankeyChart, DonutChart, StackedBarChart, DiscreteColorLegend, LineChart, makeWidthFlexible } from "@unifiedpatents/euclid-react";
import { nestedObjectsToGroupedSeries, objectToSeries, orderBy, removeOutlierByPercentile } from "@unifiedpatents/euclid-react/lib/helpers/preprocessors";
import * as Colors from "@unifiedpatents/euclid-react/lib/helpers/colors";
import ChartWithControls, { ChartControls } from "@/components/common/chart-with-controls";
import LoadingWrapper from "@/components/common/loading-wrapper";
import { renderIndustry, renderTechCenter } from "@/components/ptab/field-defs";
import { ENTITY_TYPE_MAP, renderEntityType } from "@/helpers/constants";

const COLOR_MAP_STATUS_PHASE = {
  "Adverse Judgment": Colors.blue200,
  "All Cases": Colors.grey200,
  "Final Written Decision": Colors.blue500,
  Instituted: Colors.blue400,
  Merits: Colors.grey400,
  "Not Instituted": Colors.grey400,
  Other: Colors.grey200,
  Pending: Colors.orange400,
  "Pending - Instituted": Colors.orange200,
  "Post-Institution": Colors.blue200,
  "Pre-Institution": Colors.blue300,
  Procedural: Colors.grey300,
  "Procedural Termination": Colors.grey500,
  Settlement: Colors.blue300,
  Total: Colors.grey200
};

const COLOR_MAP_INSTITUTION_OUTCOME = {
  "Not Instituted - Merits": Colors.grey400,
  "Not Instituted - Procedural": Colors.grey300,
  "Instituted - Granted": Colors.blue400
};

const COLOR_MAP_CLAIM_OUTCOME = {
  "All Claims Instituted": Colors.blue400,
  "All Claims Patentable": Colors.grey500,
  "All Claims Unpatentable": Colors.blue400,
  "Mixed Results": Colors.blue200,
  "No Claims Instituted": Colors.grey400
};

// Colour by entity family: blue = NPE, grey = operating company, green = institutional,
// orange = aggregator. Residual buckets take the lightest grey so they read as "none"
// rather than as an unsized operating company.
const COLOR_MAP_ENTITY_TYPE = {
  [ENTITY_TYPE_MAP.NPE_PAE]: Colors.blue400,
  [ENTITY_TYPE_MAP.NPE_SC]: Colors.blue300,
  [ENTITY_TYPE_MAP.NPE_I]: Colors.blue200,
  [ENTITY_TYPE_MAP.NPE]: Colors.blue100,
  [ENTITY_TYPE_MAP.OC_L]: Colors.grey500,
  [ENTITY_TYPE_MAP.OC_SM]: Colors.grey400,
  [ENTITY_TYPE_MAP.OC]: Colors.grey300,
  [ENTITY_TYPE_MAP.U]: Colors.green300,
  [ENTITY_TYPE_MAP.G]: Colors.green400,
  [ENTITY_TYPE_MAP.DA]: Colors.orange300,
  [ENTITY_TYPE_MAP.UP]: Colors.orange400,
  [ENTITY_TYPE_MAP.O]: Colors.grey100
};

const MARGIN_LEFT = { left: 160, right: 50, top: 50, bottom: 50 };
const MARGIN_MORE_LEFT = { left: 280, right: 50, top: 60, bottom: 50 };

// Hoist makeWidthFlexible wrappers to module scope — these used to be reconstructed every render via HOC composition.
const FlexibleSankeyChart = makeWidthFlexible(SankeyChart);
const FlexibleDonutChart = makeWidthFlexible(DonutChart);
const FlexibleStackedBarChart = makeWidthFlexible(StackedBarChart);
const FlexibleLineChart = makeWidthFlexible(LineChart);

const propValidation = { query: PropTypes.object };

export const CasesByPhaseChart = props => (
  <ChartWithControls
    apiCall={getCasesByPhase}
    query={props.query}
    chart={FlexibleSankeyChart}
    title="Cases by Phase and Status"
    colorMap={{ ...COLOR_MAP_STATUS_PHASE, ...COLOR_MAP_CLAIM_OUTCOME }}
    totalIndex="Total"
    totalLabel="All Cases"
    height={500}
    {...props}
  />
);
CasesByPhaseChart.propTypes = propValidation;

export const CasesByClaimOutcomeAtInstitutionChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["No Claims Instituted", "Mixed Results", "All Claims Instituted"]);
  return (
    <ChartWithControls
      apiCall={getCasesByClaimInstitutionOutcome}
      query={props.query}
      chart={FlexibleDonutChart}
      dataFormatter={dataFormatter}
      title="Outcomes at Institution Decision"
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      height={500}
      scrollable={false}
      {...props}
    />
  );
};
CasesByClaimOutcomeAtInstitutionChart.propTypes = propValidation;

export const CasesByClaimOutcomeAtFWDChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), ["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]);
  return (
    <ChartWithControls
      apiCall={getCasesByClaimDecisionOutcome}
      query={props.query}
      chart={FlexibleDonutChart}
      dataFormatter={dataFormatter}
      title="Outcomes at Final Written Decision"
      colorMap={COLOR_MAP_CLAIM_OUTCOME}
      height={500}
      scrollable={false}
      {...props}
    />
  );
};
CasesByClaimOutcomeAtFWDChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPetitionerLawfirmInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Instituted - Granted" }}
    title="Institution Rates for Petitioners' Counsel"
    colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
    {...props}
  />
);
CasesByPetitionerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPatentOwnerLawfirmInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "Not Instituted - Merits" }}
    title="Institution Rates for Patent Owners' Counsel"
    colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
    {...props}
  />
);
CasesByPatentOwnerLawfirmInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByAuthorJudgeInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByAuthorJudgeInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Institution Rates for Author Judge"
    colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
    {...props}
  />
);
CasesByAuthorJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPanelJudgeInstitutionOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPanelJudgeInstitutionOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Institution Rates for Panel Judge"
    colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
    {...props}
  />
);
CasesByPanelJudgeInstitutionOutcomeChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmPerformanceChart = props => (
  <ChartWithControls
    apiCall={getCasesByPetitionerLawfirmPerformance}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "percentage", sort: "Instituted - Granted" }}
    colorMap={{ ...COLOR_MAP_INSTITUTION_OUTCOME, ...COLOR_MAP_CLAIM_OUTCOME }}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "All Claims Patentable"]}
    {...props}
  />
);
CasesByPetitionerLawfirmPerformanceChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmPerformanceChart = props => (
  <ChartWithControls
    apiCall={getCasesByPatentOwnerLawfirmPerformance}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "percentage", sort: "Not Instituted - Merits" }}
    colorMap={{ ...COLOR_MAP_INSTITUTION_OUTCOME, ...COLOR_MAP_CLAIM_OUTCOME }}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["Instituted - Granted", "Not Instituted - Merits", "All Claims Patentable"]}
    {...props}
  />
);
CasesByPatentOwnerLawfirmPerformanceChart.propTypes = propValidation;

export const CasesByPetitionerLawfirmFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPetitionerLawfirmFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "All Claims Unpatentable" }}
    title="Final Written Decision Rates for Petitioners' Counsel"
    colorMap={COLOR_MAP_CLAIM_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
    {...props}
  />
);
CasesByPetitionerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const CasesByPatentOwnerLawfirmFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPatentOwnerLawfirmFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "All Claims Patentable" }}
    title="Final Written Decision Rates for Patent Owners' Counsel"
    colorMap={COLOR_MAP_CLAIM_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_MORE_LEFT}
    legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
    {...props}
  />
);
CasesByPatentOwnerLawfirmFWDOutcomeChart.propTypes = propValidation;

export const CasesByAuthorJudgeFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByAuthorJudgeFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Final Written Decision Rates for Author Judge"
    colorMap={COLOR_MAP_CLAIM_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
    {...props}
  />
);
CasesByAuthorJudgeFWDOutcomeChart.propTypes = propValidation;

export const CasesByPanelJudgeFWDOutcomeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPanelJudgeFWDOutcome}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "count", sort: "__total__" }}
    title="Final Written Decision Rates for Panel Judge"
    colorMap={COLOR_MAP_CLAIM_OUTCOME}
    orientation="vertical"
    showGridLines={false}
    height={750}
    margin={MARGIN_LEFT}
    legends={["All Claims Unpatentable", "Mixed Results", "All Claims Patentable"]}
    {...props}
  />
);
CasesByPanelJudgeFWDOutcomeChart.propTypes = propValidation;

export const CasesByTechCenterChart = props => (
  <ChartWithControls
    apiCall={getCasesByTechCenter}
    query={props.query}
    chart={FlexibleDonutChart}
    dataFormatter={objectToSeries}
    title="Cases by Tech Center"
    scrollable={false}
    {...props}
  />
);
CasesByTechCenterChart.propTypes = propValidation;

export const CasesByIndustryChart = props => (
  <ChartWithControls
    apiCall={getCasesByIndustry}
    query={props.query}
    chart={FlexibleDonutChart}
    dataFormatter={objectToSeries}
    title="Cases by Industry"
    scrollable={false}
    {...props}
  />
);
CasesByIndustryChart.propTypes = propValidation;

export const CasesByPOEntityTypeChart = props => {
  const dataFormatter = data => orderBy(objectToSeries(data), [ENTITY_TYPE_MAP.NPE_PAE, ENTITY_TYPE_MAP.NPE_SC, ENTITY_TYPE_MAP.NPE_I, ENTITY_TYPE_MAP.NPE, ENTITY_TYPE_MAP.OC_L, ENTITY_TYPE_MAP.OC_SM, ENTITY_TYPE_MAP.OC, ENTITY_TYPE_MAP.U, ENTITY_TYPE_MAP.G, ENTITY_TYPE_MAP.DA, ENTITY_TYPE_MAP.UP]);
  return (
    <ChartWithControls
      apiCall={getCasesByPOEntityType}
      query={props.query}
      chart={FlexibleDonutChart}
      dataFormatter={dataFormatter}
      title="Cases by P.O. Entity Type"
      colorMap={COLOR_MAP_ENTITY_TYPE}
      scrollable={false}
      {...props}
    />
  );
};
CasesByPOEntityTypeChart.propTypes = propValidation;

export const CasesByPetEntityTypeChart = props => (
  <ChartWithControls
    apiCall={getCasesByPetEntityType}
    query={props.query}
    chart={FlexibleDonutChart}
    dataFormatter={objectToSeries}
    title="Cases by Pet. Entity Type"
    colorMap={COLOR_MAP_ENTITY_TYPE}
    scrollable={false}
    {...props}
  />
);
CasesByPetEntityTypeChart.propTypes = propValidation;

export const CasesByPOEntityTypeOverYearsChart = props => (
  <ChartWithControls
    apiCall={getCasesByPOEntityTypeOverYears}
    query={props.query}
    chart={FlexibleStackedBarChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    title="NPE v.s. Non-NPE Patent Owners"
    colorMap={COLOR_MAP_ENTITY_TYPE}
    // Legend entries render whether or not the data has rows, so list only the up_po_type
    // vocabulary (see PO_ENTITY_TYPES in ../../toolbar) — the petitioner-only and rowless
    // codes would show as permanently empty categories.
    legends={[ENTITY_TYPE_MAP.NPE_PAE, ENTITY_TYPE_MAP.NPE_SC, ENTITY_TYPE_MAP.NPE_I, ENTITY_TYPE_MAP.OC, ENTITY_TYPE_MAP.U, ENTITY_TYPE_MAP.G]}
    {...props}
  />
);
CasesByPOEntityTypeOverYearsChart.propTypes = propValidation;

export const CasesByTypeOverYearsChart = props => {
  const colorMap = {
    DER: Colors.grey200,
    PGR: Colors.grey400,
    IPR: Colors.blue400,
    CBM: Colors.blue200
  };
  return (
    <ChartWithControls
      apiCall={getCasesByTypeOverYears}
      query={props.query}
      chart={FlexibleStackedBarChart}
      dataFormatter={nestedObjectsToGroupedSeries}
      title="Cases Per Year by Case Type"
      colorMap={colorMap}
      legends={["IPR", "CBM", "PGR", "DER"]}
      scrollable={false}
      {...props}
    />
  );
};
CasesByTypeOverYearsChart.propTypes = propValidation;

export const CasesByInstitutionOutcomeOverYearsChart = props => (
  <ChartWithControls
    apiCall={getCasesByInstitutionOutcomeOverYears}
    query={props.query}
    chart={FlexibleLineChart}
    dataFormatter={nestedObjectsToGroupedSeries}
    defaults={{ show: "percentage" }}
    title="Institution Rate"
    colorMap={COLOR_MAP_INSTITUTION_OUTCOME}
    legends={["Instituted - Granted", "Not Instituted - Merits", "Not Instituted - Procedural"]}
    {...props}
  />
);
CasesByInstitutionOutcomeOverYearsChart.propTypes = propValidation;

// DateHistogram is data-shaped differently from the standard ChartWithControls flow (it does its
// own reformat + per-render colorScale build), so it uses <ChartControls> with parent-supplied data
// instead of letting ChartWithControls own the fetch.

const callToClick = <a>Click to View Cases</a>;

// up_industry aggregation keys come back lowercase; INDUSTRY_MAP is keyed T/M/O.
const mapDrilldownKey = (k, field2) => {
  if (field2 === "up_industry") return renderIndustry(`${k}`.toUpperCase());
  if (field2 === "up_pet_type") return renderEntityType(k);
  if (field2 === "ptab_tech_center") return renderTechCenter(k);
  if (field2 === "type") return `${k}`.toUpperCase();
  if (field2 === "up_po_type") return renderEntityType(k);
  return `${k}`;
};

const yearLabel = ts => `${new Date(ts).getUTCFullYear()}`;

const reformatDateHistogram = (data, field2) => {
  if (field2) {
    const formatted = {};
    data.forEach(e => {
      const sub = {};
      let subSum = 0;
      e.value.forEach(bb => {
        const label = mapDrilldownKey(bb.key, field2);
        sub[label] = (sub[label] || 0) + bb.doc_count;
        subSum += bb.doc_count;
      });
      // Year total minus named sub-buckets = docs with null/missing field2 plus any terms-tail overflow.
      const other = e.doc_count - subSum;
      if (other > 0) sub.Other = (sub.Other || 0) + other;
      formatted[yearLabel(e.key)] = sub;
    });
    const grouped = nestedObjectsToGroupedSeries(formatted);
    const otherIdx = grouped.findIndex(g => g.k === "Other");
    if (otherIdx >= 0) grouped.unshift(grouped.splice(otherIdx, 1)[0]);
    return removeOutlierByPercentile(grouped, 99);
  }
  const flat = data.map(e => ({ k: yearLabel(e.key), v: e.doc_count }));
  return [{ k: "Count", v: removeOutlierByPercentile(flat, 99) }];
};

// Fresh color scale per drill-down selection so series ordering deterministically
// picks colors from the start of the palette instead of inheriting state from a
// long-lived module-level scale.
const buildDateHistogramColorMap = data => {
  const scale = Colors.colorScaleCategorical();
  const colorMap = {};
  data.forEach(g => {
    if (g.k === "Other") colorMap[g.k] = Colors.grey300;
    else colorMap[g.k] = scale(g.k);
  });
  return colorMap;
};

export const DateHistogram = ({ field, field2, filter = {}, onSelectBar }) => {
  const apiCall = field2 ? getCasesByDateAndField : getCasesByDate;
  const { data: raw, isLoading } = useSWR(
    [apiCall, "dateHistogram", field, field2, JSON.stringify(filter)],
    () => (field2 ? getCasesByDateAndField(filter, field, field2) : getCasesByDate(filter, field))
  );

  const { data, colorMap } = useMemo(() => {
    if (!raw) return { data: [], colorMap: {} };
    const reshaped = reformatDateHistogram(raw, field2);
    return { data: reshaped, colorMap: buildDateHistogramColorMap(reshaped) };
  }, [raw, field2]);

  return (
    <LoadingWrapper loading={isLoading} spinSize="large" style={{ minHeight: 400, textAlign: "center" }}>
      <ChartControls
        data={data}
        chart={FlexibleStackedBarChart}
        colorMap={colorMap}
        height={500}
        tickLabelAngleX={-30}
        tickFormatX={v => v}
        onValueClick={onSelectBar ? v => onSelectBar(v.key) : undefined}
        tooltipExtraRow={onSelectBar ? callToClick : undefined}
      />
      {field2 && data.length > 0 && (
        <DiscreteColorLegend orientation="horizontal" items={data.map(d => d.k)} colors={data.map(d => colorMap[d.k])} />
      )}
    </LoadingWrapper>
  );
};
DateHistogram.propTypes = {
  field: PropTypes.string.isRequired,
  field2: PropTypes.string,
  filter: PropTypes.object,
  onSelectBar: PropTypes.func
};
